// upload.ts — the server-mode uploader (ARCHITECTURE.md §15.3). After the
// switch it POSTs video/start with the decision numbers and the lens prior,
// then uploads every recorded chunk — the device-mode backlog first, then
// each new one as it arrives — with PUT video/chunks/{seq}: idempotent by
// seq, retried with capped exponential backoff, at most 3 in flight, lowest
// seq first. IMU samples (performance.now() clock, the recorder's) go in ~1 s
// batches to POST /imu; their failures are logged, never fatal (the server
// tracks vision-only without them). finish() waits for every chunk, flushes
// the IMU and POSTs video/finish {last_seq}. Chunks and finish carry the
// upload id from video/start as ?upload_id= (§15.4.1). A chunk that exhausts its
// retries, or a non-retryable 4xx, fails the upload: finish() then rejects.
// The REST calls are injected so the queue logic unit-tests without a server.

import type { ImuBatchRequest, Scan, VideoStartRequest, VideoStartResponse } from '../api';
import type { VideoChunk } from './recorder';
import type { ImuSampleJSON } from './worker';

export const MAX_IN_FLIGHT = 3;
export const MAX_ATTEMPTS = 8;
export const RETRY_MIN_MS = 500;
export const RETRY_MAX_MS = 15000;
export const IMU_FLUSH_MS = 1000;
export const IMU_MAX_BATCH = 2000;

export interface UploadApi {
  videoStart(id: string, body: VideoStartRequest): Promise<VideoStartResponse>;
  putVideoChunk(id: string, seq: number, chunk: Blob, uploadId: string): Promise<void>;
  postImu(id: string, body: ImuBatchRequest): Promise<void>;
  videoFinish(id: string, lastSeq: number, uploadId: string): Promise<Scan | null>;
}

export interface UploadStats {
  chunksUploaded: number;
  bytesUploaded: number;
  queued: number;
  inFlight: number;
  retries: number;
  imuSent: number;
  failed: string; // '' while healthy
}

// retryDelay is the wait before attempt n+1 (n ≥ 1), with ±25% jitter.
export function retryDelay(attempt: number, rand: () => number = Math.random): number {
  const base = Math.min(RETRY_MAX_MS, RETRY_MIN_MS * 2 ** Math.max(0, attempt - 1));
  return Math.round(base * (0.75 + 0.5 * rand()));
}

// isRetryable: network errors, 408/425/429 and 5xx are worth retrying; any
// other HTTP status (a 4xx the server meant) is not.
export function isRetryable(err: unknown): boolean {
  const status = (err as { status?: unknown })?.status;
  if (typeof status !== 'number') return true;
  return status === 408 || status === 425 || status === 429 || status >= 500;
}

// splitBatches cuts samples into POST-sized batches.
export function splitBatches<T>(samples: readonly T[], max = IMU_MAX_BATCH): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < samples.length; i += max) out.push(samples.slice(i, i + max));
  return out;
}

export interface UploaderOptions {
  maxInFlight?: number;
  maxAttempts?: number;
  sleep?: (ms: number) => Promise<void>;
  rand?: () => number;
  onStats?: (s: UploadStats) => void;
  onError?: (message: string) => void;
  imuFlushMs?: number; // 0 disables the timer (tests flush by hand)
}

const errMsg = (err: unknown): string => (err instanceof Error ? err.message : String(err));

export class VideoUploader {
  private queue: VideoChunk[] = [];
  private seen = new Set<number>();
  private inFlight = 0;
  private started = false;
  private closed = false;
  private failure: Error | null = null;
  private idleWaiters: Array<{ resolve: () => void; reject: (e: Error) => void }> = [];
  private imuBuf: ImuSampleJSON[] = [];
  private imuAngle = 0;
  private imuChain: Promise<void> = Promise.resolve();
  private imuTimer: ReturnType<typeof setInterval> | undefined;
  private readonly maxInFlight: number;
  private readonly maxAttempts: number;
  private readonly sleep: (ms: number) => Promise<void>;
  private readonly rand: () => number;
  private stats: UploadStats = { chunksUploaded: 0, bytesUploaded: 0, queued: 0, inFlight: 0, retries: 0, imuSent: 0, failed: '' };
  uploadId = '';

  constructor(readonly scanId: string, private api: UploadApi, private opts: UploaderOptions = {}) {
    this.maxInFlight = opts.maxInFlight ?? MAX_IN_FLIGHT;
    this.maxAttempts = opts.maxAttempts ?? MAX_ATTEMPTS;
    this.sleep = opts.sleep ?? ((ms) => new Promise((r) => setTimeout(r, ms)));
    this.rand = opts.rand ?? Math.random;
  }

  get snapshot(): UploadStats {
    return { ...this.stats, queued: this.queue.length, inFlight: this.inFlight };
  }

  private emit(): void {
    if (!this.closed) this.opts.onStats?.(this.snapshot);
  }

  // retry runs fn until it succeeds, fails non-retryably or runs out of attempts.
  private async retry<T>(what: string, fn: () => Promise<T>): Promise<T> {
    for (let attempt = 1; ; attempt++) {
      try {
        return await fn();
      } catch (err) {
        if (this.closed) throw err;
        if (!isRetryable(err) || attempt >= this.maxAttempts) {
          console.error(`[openscan/upload] ${what} failed for good after ${attempt} attempt(s): ${errMsg(err)}`);
          throw err;
        }
        const d = retryDelay(attempt, this.rand);
        this.stats.retries++;
        console.warn(`[openscan/upload] ${what} attempt ${attempt} failed (${errMsg(err)}); retrying in ${d} ms`);
        this.emit();
        await this.sleep(d);
      }
    }
  }

  // begin POSTs video/start and starts draining the chunk queue.
  async begin(body: VideoStartRequest): Promise<string> {
    console.info(`[openscan/upload] video/start scan=${this.scanId} mime=${body.mime} ${body.width}x${body.height} camera=${body.camera_fps} fps processing=${body.processing_fps} fps prior=${body.prior_intrinsics ? 'yes' : 'no'} live=${body.live ? 'yes' : 'no'}`);
    const res = await this.retry('video/start', () => this.api.videoStart(this.scanId, body));
    this.uploadId = res?.upload_id ?? '';
    this.started = true;
    console.info(`[openscan/upload] upload ${this.uploadId || '(no id)'} started; ${this.queue.length} chunks queued`);
    const ms = this.opts.imuFlushMs ?? IMU_FLUSH_MS;
    clearInterval(this.imuTimer);
    if (ms > 0) this.imuTimer = setInterval(() => void this.flushImu(), ms);
    this.pump();
    void this.flushImu();
    return this.uploadId;
  }

  // addChunk queues one chunk (duplicates by seq are ignored).
  addChunk(c: VideoChunk): void {
    if (this.closed || this.seen.has(c.seq)) return;
    this.seen.add(c.seq);
    this.queue.push(c);
    this.queue.sort((a, b) => a.seq - b.seq);
    this.pump();
    this.emit();
  }

  // addImu buffers samples for the next batch POST.
  addImu(samples: readonly ImuSampleJSON[], screenAngle: number): void {
    if (this.closed || samples.length === 0) return;
    for (const s of samples) this.imuBuf.push(s);
    this.imuAngle = screenAngle;
  }

  // flushImu POSTs the buffered samples in order (best effort).
  flushImu(): Promise<void> {
    if (!this.started || this.closed || this.imuBuf.length === 0) return this.imuChain;
    const batches = splitBatches(this.imuBuf);
    const angle = this.imuAngle;
    this.imuBuf = [];
    this.imuChain = this.imuChain.then(async () => {
      for (const samples of batches) {
        try {
          await this.retry(`imu batch (${samples.length})`, () => this.api.postImu(this.scanId, { samples, screen_angle: angle }));
          this.stats.imuSent += samples.length;
          console.debug(`[openscan/upload] imu +${samples.length} (total ${this.stats.imuSent})`);
        } catch (err) {
          if (!this.closed) console.warn(`[openscan/upload] dropping ${samples.length} IMU samples: ${errMsg(err)}`);
        }
      }
      this.emit();
    });
    return this.imuChain;
  }

  private pump(): void {
    if (!this.started || this.closed || this.failure) return;
    while (this.inFlight < this.maxInFlight && this.queue.length) {
      const c = this.queue.shift()!;
      this.inFlight++;
      void this.uploadOne(c);
    }
    this.settleIdle();
  }

  private async uploadOne(c: VideoChunk): Promise<void> {
    const t0 = performance.now();
    try {
      await this.retry(`chunk ${c.seq}`, () => this.api.putVideoChunk(this.scanId, c.seq, c.blob, this.uploadId));
      this.stats.chunksUploaded++;
      this.stats.bytesUploaded += c.blob.size;
      console.debug(`[openscan/upload] chunk ${c.seq} ${c.blob.size} B in ${Math.round(performance.now() - t0)} ms (${this.queue.length} queued, ${this.inFlight - 1} in flight)`);
    } catch (err) {
      if (!this.closed && !this.failure) {
        this.failure = new Error(`video chunk ${c.seq} upload failed: ${errMsg(err)}`);
        this.stats.failed = this.failure.message;
        this.opts.onError?.(this.failure.message);
      }
    } finally {
      this.inFlight--;
      this.emit();
      this.pump();
      this.settleIdle();
    }
  }

  private settleIdle(): void {
    if (this.failure) {
      this.idleWaiters.splice(0).forEach((w) => w.reject(this.failure!));
      return;
    }
    if (this.started && this.inFlight === 0 && this.queue.length === 0) this.idleWaiters.splice(0).forEach((w) => w.resolve());
  }

  // drained resolves once every queued chunk is uploaded (rejects on failure).
  drained(): Promise<void> {
    if (this.failure) return Promise.reject(this.failure);
    return new Promise<void>((resolve, reject) => {
      this.idleWaiters.push({ resolve, reject });
      this.settleIdle();
    });
  }

  // finish uploads what is left, flushes the IMU and POSTs video/finish.
  async finish(lastSeq: number): Promise<Scan | null> {
    if (!this.started) throw new Error('video upload was never started');
    console.info(`[openscan/upload] finishing: last_seq=${lastSeq}, ${this.queue.length} queued, ${this.inFlight} in flight`);
    await this.drained();
    await this.flushImu();
    const scan = await this.retry('video/finish', () => this.api.videoFinish(this.scanId, lastSeq, this.uploadId));
    console.info(`[openscan/upload] video/finish ok: ${this.stats.chunksUploaded} chunks, ${Math.round(this.stats.bytesUploaded / 1024)} KiB, ${this.stats.imuSent} IMU samples`);
    return scan;
  }

  // healthy: no chunk has failed for good and the uploader is open, so a
  // finish() whose final POST failed may be called again.
  get healthy(): boolean {
    return !this.failure && !this.closed;
  }

  close(): void {
    this.closed = true;
    clearInterval(this.imuTimer);
    this.queue = [];
    this.idleWaiters.splice(0).forEach((w) => w.reject(new Error('uploader closed')));
    console.info('[openscan/upload] closed');
  }
}
